import React, {useEffect} from 'react';
import { Button, Field, Input, MessageBar, MessageBarBody, Spinner } from '@fluentui/react-components';
import {AddToCartButton} from "./SearchPage";
import BlockedCartNotice from '../Components/Cart/BlockedCartNotice';
import CurrencyFormat from '../Components/Helpers/CurrencyFormat';
import { useParams } from 'react-router-dom';
import { useGetBundleQuery, useGetStorefrontQuery } from '../Api/Api';
import { useBundleConfiguration, stripNonNumeric } from '../Components/Bundle/useBundleConfiguration';
import BundleOptionsTables from '../Components/Bundle/BundleOptionsTables';
import { parseBundlePageConfig } from '../Components/Helpers/bundlePageConfig';
import { elementStyleFrom, importantStyleRef } from '../Components/Helpers/searchPageConfig';
import CollapsibleDescription from '../Components/Product/CollapsibleDescription';
import ProductImageModal from '../Components/Product/ProductImageModal';
import FullPageSpinner from '../Components/Spinner/FullPageSpinner';
import { ProductImage } from "../Components/Helpers/ProductImage";
import {BoxMultipleSearchFilled, HomeFilled} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import CAN, {PRICING, CART_MANAGEMENT} from "../Components/Auth/Permissions";
import {useSelector} from "react-redux";
import {RootState} from "../Store/Store";
import {useGetPreparedCartQuery} from "../Api/PreparedCartEndpoints";
import { useLocalization } from '../Components/Helpers/useLocalization';

const BundlePage: React.FC = () => {
    const L = useLocalization();
    const canSeePricing = CAN(PRICING);
    const canAddToCart = CAN(CART_MANAGEMENT);

    const { bundleId } = useParams<{ bundleId: string }>();
    const settings = useSelector((state: RootState) => state.cart);
    const bundle = useGetBundleQuery({ bundleId: bundleId ?? '' });
    const [quantity, setQuantity] = React.useState<number>(1);
    const [tmpQuantity, setTmpQuantity] = React.useState<string>('1');
    const [imageModalOpen, setImageModalOpen] = React.useState<boolean>(false);
    const [warningMessage, setWarningMessage]= React.useState<string>('');
    const getPreparedCart = useGetPreparedCartQuery({cartId: settings.selectedCartId ?? ''}, {skip: !settings.selectedCartId});
    const [canActuallyAddToCart, setCanActuallyAddToCart] = React.useState(false);
    const [cartBlocked, setCartBlocked] = React.useState(false);
    const navigate = useNavigate();

    // Grouped choices, optional inclusions, per-item quantities, and the server-estimated price/selection —
    // shared with the cart edit panel via this hook. bundleQuantity is passed so the estimate call
    // reflects the real order quantity (floor/margin rules in ac-main are quantity-sensitive).
    const config = useBundleConfiguration(bundle.data, { bundleQuantity: quantity });

    // Merchant bundle-page styling (bundlePageConfig JSON). Each section applies to a bounding
    // component; theme-forced elements (the included-products table) get an !important ref, plain
    // boxes inline. Absent config = zero inline styles (strictly additive).
    const storefrontSettings = useGetStorefrontQuery();
    const bundleConfig = React.useMemo(() => parseBundlePageConfig(storefrontSettings.data?.bundlePageConfig), [storefrontSettings.data?.bundlePageConfig]);
    const containerStyle = React.useMemo(() => elementStyleFrom(bundleConfig.container), [bundleConfig.container]);
    const titleStyle = React.useMemo(() => elementStyleFrom(bundleConfig.title, 'heading'), [bundleConfig.title]);
    const imageStyle = React.useMemo(() => elementStyleFrom(bundleConfig.image, 'image'), [bundleConfig.image]);
    const priceStyle = React.useMemo(() => elementStyleFrom(bundleConfig.price, 'price'), [bundleConfig.price]);
    const quantityStyle = React.useMemo(() => elementStyleFrom(bundleConfig.quantity, 'input'), [bundleConfig.quantity]);
    const addToCartStyle = React.useMemo(() => elementStyleFrom(bundleConfig.addToCartButton), [bundleConfig.addToCartButton]);
    const addToCartLabelStyle = React.useMemo(() => elementStyleFrom(bundleConfig.addToCartLabel, 'label'), [bundleConfig.addToCartLabel]);
    const descriptionStyle = React.useMemo(() => elementStyleFrom(bundleConfig.description, 'body'), [bundleConfig.description]);
    const includedTableRef = React.useMemo(() => { const st = elementStyleFrom(bundleConfig.includedTable); return Object.keys(st).length ? importantStyleRef(st) : undefined; }, [bundleConfig.includedTable]);

    useEffect(() => {
        setTmpQuantity(quantity.toString());
    }, [quantity]);

    useEffect(() => {
        if(settings.selectedCartId && getPreparedCart.data) {
            const canAddToPreparedCart = getPreparedCart.data.canAddAdditionalItems ?? false;
            setCanActuallyAddToCart(canAddToPreparedCart && canAddToCart);
            setCartBlocked(!canAddToPreparedCart);
        }
        else{
            setCanActuallyAddToCart(canAddToCart);
            setCartBlocked(false);
        }
    }, [canAddToCart, getPreparedCart.data, settings.selectedCartId]);

    const trySetQuantity = (qty: number) => {
        if(qty < 1){
            setWarningMessage('Quantity must be at least 1');
            setQuantity(1);
        }
        else if(bundle?.data?.maxQuantity && qty > bundle.data.maxQuantity) {
            setWarningMessage(`Only ${bundle.data.maxQuantity} available for purchase`)
            setQuantity(bundle.data.maxQuantity)
        }
        else{
            setWarningMessage('');
            setQuantity(qty);
        }
    }

    const trySetTmpQuantity = (value: string) => {
        const numericValue = stripNonNumeric(value);
        if(numericValue === '') {
            setWarningMessage('Quantity cannot be empty');
            setTmpQuantity('');
            return;
        }
        const parsedQuantity = parseInt(numericValue);
        if(isNaN(parsedQuantity)){
            setWarningMessage(`Quantity must be a number`);
            return;
        }
        else if(parsedQuantity < 1) {
            setWarningMessage(`Quantity must be a number greater than 0`);
            setTmpQuantity('1');
        } else {
            setWarningMessage('');
            setTmpQuantity(numericValue);
        }
    }

    const blur = () => {
        let parsedQuantity = parseInt(tmpQuantity);
        if(parsedQuantity < 1) {parsedQuantity = 1}
        trySetQuantity(isNaN(parsedQuantity) ? 1 : parsedQuantity);
    }
    
    // Prepared carts send no selection; a regular cart gets the configured selection from the hook.
    const bundleSelection = config.bundleSelection;

    if (bundle.isLoading || bundle.isFetching) return <FullPageSpinner message={L('localization_common_loading')} />

    return (
        <div className='sf-detail-page' style={{width:'100%', maxWidth: '800px', margin: '0 auto', ...containerStyle}}>

            {bundle.data ?
            <div>
                <h1 style={{lineHeight: 1.3, ...titleStyle}}>{bundle.data.name}</h1>
                {/* Top portion mirrors the product page: image on the left, price / quantity /
                    add-to-cart in a right-hand data column. The included-items list and the
                    description follow below, after the float is cleared. */}
                <ProductImage
                    style={{marginBottom: '1em', ...imageStyle}}
                    src={bundle.data?.image}
                    // No real image means the fallback placeholder is showing — nothing worth enlarging.
                    className={`product-image${bundle.data.image ? ' product-image-zoomable' : ''}`}
                    onClick={bundle.data.image ? () => setImageModalOpen(true) : undefined}
                />
                <div className='product-data'>
                    {canSeePricing && (
                        <h2 className='product-price' style={priceStyle}>
                            {config.isEstimating
                                ? <Spinner size='tiny' />
                                : <CurrencyFormat value={config.estimatedPrice ?? bundle.data.price} />}
                        </h2>
                    )}
                    {canActuallyAddToCart &&
                        <Field style={{marginBottom: '1em', marginTop: '1em'}}>
                            <Input
                                contentBefore={<>Quantity: </>}
                                style={{width: '100%', textAlign: 'right', ...quantityStyle}}
                                onBlur={blur}
                                value={tmpQuantity}
                                onChange={(_,d) => trySetTmpQuantity(d.value)}
                            />
                        </Field>
                    }
                    {warningMessage &&
                        <MessageBar intent={"warning"}>
                            <MessageBarBody>
                                {warningMessage}
                            </MessageBarBody>
                        </MessageBar>
                    }
                    {canActuallyAddToCart && bundle.data &&
                        <div
                            onClick={e => e.stopPropagation()}
                            className="card-add-to-cart"
                            style={{
                                zIndex: 3,
                                background: 'white',
                                borderRadius: 13,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                padding: 0,
                                cursor: 'pointer',
                                display: 'inline-flex',
                            }}
                        >
                            <AddToCartButton product={{ featured: false, id: bundle.data.id ?? '', catalog: '', bundleId: bundleId }} promptOnAdd quantity={quantity} buttonStyle={addToCartStyle} labelStyle={addToCartLabelStyle} bundleSelection={bundleSelection} source="bundle" />
                        </div>
                    }
                    {cartBlocked && <BlockedCartNotice />}
                </div>
                <div style={{clear: 'both'}} />
                <BundleOptionsTables
                    bundle={bundle.data}
                    config={config}
                    canSeePricing={canSeePricing}
                    disabled={!canActuallyAddToCart}
                    includedTableRef={includedTableRef}
                />
                <div style={{paddingTop: '1em'}}>
                    {bundle.data.description &&
                        <CollapsibleDescription
                            markdown={bundle?.data?.description ?? ''}
                            className='product-description'
                            style={descriptionStyle}
                            collapsible={!!bundleConfig.descriptionCollapsible}
                            collapsedHeight={bundleConfig.descriptionCollapsedHeight}
                        />
                    }
                </div>
                <ProductImageModal
                    open={imageModalOpen}
                    onOpenChange={setImageModalOpen}
                    images={bundle.data.image ? [bundle.data.image] : []}
                    index={0}
                    onIndexChange={() => {}}
                    title={bundle.data.name}
                />
            </div> :
            <div className={'centered-content'}>
                <BoxMultipleSearchFilled style={{fontSize: '24em', color: '#ddd', paddingBottom: '20px'}} />
                <p><h1>Bundle Not Found</h1></p>
                <Button
                    icon={<HomeFilled/>}
                    appearance='primary'
                    style={{width: '25%', marginTop: '1em', maxWidth:'200px'}}
                    onClick={() => navigate('/')}
                >
                    {L('localization_common_go_home')}
                </Button>
            </div>}
        </div>
    );
}

export default BundlePage;
